import { AlertTriangle, ArrowUpRight, Check, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { Badge, Button, Input, Textarea } from '@/components/ui/primitives.tsx'
import type { PendingApproval } from '@/hooks/use-run-stream.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'
import { riskLabel } from './approval-labels.ts'

/**
 * The human decision point.
 *
 * Two things make this a control rather than a confirmation dialog. The exact payload is
 * shown in full — not summarized — because a person cannot consent to something they were
 * only told about; and the decision carries the `digest` back to the server, which
 * recomputes it. If anything about the proposal changed between display and click, the
 * decision is refused rather than applied to a different action.
 *
 * Editing before approving is offered where it is safe: the edited arguments are
 * re-validated against the tool's own schema server-side, so a person can fix a wrong
 * recipient without restarting the whole run.
 */
export const ApprovalCard = ({
  approval,
  onDecide,
  busy
}: {
  approval: PendingApproval
  onDecide: (decision: {
    decision: 'approved' | 'rejected' | 'edited_and_approved'
    digest: string
    editedArguments?: Record<string, unknown>
    note?: string
  }) => void
  busy?: boolean
}) => {
  // Inside the component body, never at module scope: a capture taken at import freezes the
  // locale and then renders the previous language with no error at all (PRD-27 F-3).
  const { formatUntil, formatList, t: messages } = useI18n()
  const t = messages.approvals
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(() => JSON.stringify(approval.arguments, null, 2))
  const [parseError, setParseError] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const submitEdited = () => {
    try {
      const parsed = JSON.parse(draft) as Record<string, unknown>
      setParseError(null)
      onDecide({
        decision: 'edited_and_approved',
        digest: approval.digest,
        editedArguments: parsed,
        note: note || undefined
      })
    } catch (err) {
      // A `JSON.parse` failure, in whatever words the engine wrote it: it is a parser's line
      // about a character offset, not a refusal a product ever authored, so it passes through
      // `describeApiError` unchanged rather than being translated into something it is not
      // (PRD-27 D-4 — the same seam, honestly applied to a failure that has no key and never
      // will).
      setParseError(describeApiError(messages, err))
    }
  }

  return (
    /* Its own surface, not a `Panel`: the alert wash has to be the card's background rather
       than a class fighting the primitive's own `bg-card` for the same property. */
    <div className="rise rounded-card border border-alert/35 bg-alert-soft shadow-glass">
      <header className="flex items-start justify-between gap-3 border-b border-rule px-4 py-3.5">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-alert" aria-hidden />
          <div>
            <h3 className="text-[14px]">{t.needsApproval(approval.displayName)}</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">{approval.rationale}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge tone="alert">{riskLabel(t, approval.risk)}</Badge>
          {!approval.reversible && <Badge tone="alert">{t.irreversible}</Badge>}
        </div>
      </header>

      <div className="space-y-3 px-4 py-3.5">
        {approval.dataLeavingPlatform.length > 0 && (
          <p className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
            <ArrowUpRight size={13} className="shrink-0 text-alert" aria-hidden />
            {t.dataReaching} <span className="font-semibold">{formatList(approval.dataLeavingPlatform)}</span>
          </p>
        )}

        <div>
          <p className="eyebrow mb-1.5">{t.payloadTitle}</p>
          {editing ? (
            <>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={10}
                spellCheck={false}
                className="font-mono text-[12.5px]"
                aria-label={t.editLabel}
              />
              {parseError && <p className="readout mt-1 text-alert">{t.invalidJson(parseError)}</p>}
            </>
          ) : (
            <pre className="max-h-64 overflow-auto rounded-tile border border-rule bg-card-solid px-3 py-2.5 text-[12.5px] leading-relaxed">
              <code className="font-mono">{JSON.stringify(approval.arguments, null, 2)}</code>
            </pre>
          )}
        </div>

        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t.notePlaceholder}
          aria-label={t.noteLabel}
        />

        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button variant="primary" size="sm" icon={<Check size={13} />} onClick={submitEdited} disabled={busy}>
                {t.approveEdited}
              </Button>
              <Button size="sm" onClick={() => setEditing(false)} disabled={busy}>
                {t.cancelEdit}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={<Check size={13} />}
                disabled={busy}
                onClick={() => onDecide({ decision: 'approved', digest: approval.digest, note: note || undefined })}
              >
                {t.approve}
              </Button>
              <Button size="sm" icon={<Pencil size={13} />} onClick={() => setEditing(true)} disabled={busy}>
                {t.editFirst}
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={<X size={13} />}
                disabled={busy}
                onClick={() => onDecide({ decision: 'rejected', digest: approval.digest, note: note || undefined })}
              >
                {t.reject}
              </Button>
            </>
          )}
          {/* `formatUntil`, not `formatRelative`: the latter clamps every future instant to
              "just now", which read as though each approval were about to lapse. */}
          <span className="readout ml-auto text-fg-subtle">{t.expires(formatUntil(approval.expiresAt))}</span>
        </div>
      </div>
    </div>
  )
}
